import { AspectRatio, Box, Flex, Link, Text, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import CtaButton from './CtaButton';
import { videoData } from './Videos';
import { RESOURCES_SUBTITLE } from '../data/resourcesData';

// Create motion components
const MotionFlex = motion(Flex);
const MotionBox = motion(Box);

// The first video in the gallery is featured on the home page
const featuredVideo = videoData[0];

/**
 * "Featured" label and the video's title, which opens it on YouTube
 */
function FeaturedTitle() {
	return (
		<Flex
			flexDirection={"column"}
			alignItems={"flex-start"}
			gap={1}
			textAlign={"left"}
			fontFamily={"Montserrat"}
		>
			<Text
				fontSize={"xs"}
				fontWeight={600}
				letterSpacing={"wider"}
				textTransform={"uppercase"}
				color={"yellow.100"}
			>
				Featured
			</Text>
			<Link
				href={`https://www.youtube.com/watch?v=${featuredVideo.videoId}`}
				isExternal
				fontSize={"lg"}
				fontWeight={600}
				color={"white.500"}
				transition={"color 0.2s ease"}
				_hover={{ textDecoration: "none", color: "yellow.100" }}
			>
				{featuredVideo.title}
			</Link>
		</Flex>
	);
}

/**
 * Home page teaser for the Resources page: title, subtitle, and CTA beside a featured video
 */
export default function ResourcesPreview() {

	const titleFontSize = useBreakpointValue({ base: "32px", md: "36px", lg: "40px" });

	return (
		<Flex
			width={"100%"}
			backgroundColor={"blue.100"}
		>
			<Flex
				width={{ base: "90%", lg: "80%" }}
				maxWidth={"1200px"}
				margin={"auto"}
				paddingY={{ base: 10, md: 16 }}
				flexDirection={{ base: "column", md: "row" }}
				alignItems={"center"}
				gap={{ base: 8, md: 12 }}
			>
				{/* Title, Subtitle (+ featured title and CTA on desktop) */}
				<MotionFlex
					flex={2}
					flexDirection={"column"}
					alignItems={{ base: "center", md: "flex-start" }}
					textAlign={{ base: "center", md: "left" }}
					gap={5}
					initial={{ opacity: 0, x: -50 }}
					whileInView={{ opacity: 1, x: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
				>
					<Text
						fontSize={titleFontSize}
						fontFamily={"Martel"}
						fontWeight={"700"}
						color={"white.500"}
					>
						Resources
					</Text>

					<Text
						fontSize={"18px"}
						fontFamily={"Montserrat"}
						fontWeight={"500"}
						color={"white.500"}
					>
						{RESOURCES_SUBTITLE}
					</Text>

					{/* Desktop: featured title and CTA beside the video */}
					<Flex
						display={{ base: "none", md: "flex" }}
						flexDirection={"column"}
						alignItems={"flex-start"}
						gap={5}
					>
						<FeaturedTitle />
						<CtaButton to={"/resources"}>
							View Resources
						</CtaButton>
					</Flex>
				</MotionFlex>

				{/* Featured Video */}
				<MotionBox
					flex={3}
					width={"100%"}
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
				>
					<AspectRatio
						ratio={16 / 9}
						borderRadius={"xl"}
						overflow={"hidden"}
						boxShadow={"lg"}
					>
						<iframe
							src={`https://www.youtube.com/embed/${featuredVideo.videoId}`}
							title={featuredVideo.title}
							style={{ border: 0 }}
							allow={"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"}
							allowFullScreen
							loading={"lazy"}
						/>
					</AspectRatio>

					{/* Mobile: featured title as a caption under the video */}
					<Box display={{ base: "block", md: "none" }} marginTop={3}>
						<FeaturedTitle />
					</Box>
				</MotionBox>

				{/* Mobile: CTA closes the section */}
				<Box display={{ base: "block", md: "none" }}>
					<CtaButton to={"/resources"}>
						View Resources
					</CtaButton>
				</Box>
			</Flex>
		</Flex>
	);
}
